import { useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import { getChartSourceSnapshot } from "@/lib/lytick/chart-source-service";
import { getBaseColumns } from "@/lib/lytick/chart-data-service";
import { sliceToCandles, searchTime } from "@/lib/lytick/columnar";
import { normalizeCandles, parseTimeframeToMinutes } from "@/lib/lytick/replay-utils";
import { useChartStore } from "@/stores/chartStore";
import { useReplayStore } from "@/stores/replayStore";

/**
 * When the user switches timeframes during replay, refresh context candles from
 * the already-loaded CSV data. No network fetch — aggregation happens client-side.
 */
export function useReplayContextRefresh() {
  const timeframe = useChartStore((s) => s.timeframe);
  const { isActive, startDate, contextGranularitySeconds, isRefreshingContext } =
    useReplayStore(
      useShallow((s) => ({
        isActive: s.isActive,
        startDate: s.startDate,
        contextGranularitySeconds: s.contextGranularitySeconds,
        isRefreshingContext: s.isRefreshingContext,
      })),
    );

  const prevTimeframeRef = useRef(timeframe);

  useEffect(() => {
    if (!isActive || !startDate || isRefreshingContext) return;

    const prevTf = prevTimeframeRef.current;
    prevTimeframeRef.current = timeframe;

    if (prevTf === timeframe) return;

    const newGranularityMinutes = parseTimeframeToMinutes(timeframe);
    if (newGranularityMinutes == null) return;
    const newGranularitySeconds = newGranularityMinutes * 60;

    if (newGranularitySeconds === contextGranularitySeconds) return;

    const state = useReplayStore.getState();
    state.refreshContextAtGranularity(newGranularitySeconds);

    try {
      const snapshot = getChartSourceSnapshot();
      if (!snapshot.hasData) {
        useReplayStore.getState().clearRefreshingContext();
        return;
      }

      const base = getBaseColumns();
      if (!base || base.length === 0) {
        useReplayStore.getState().clearRefreshingContext();
        return;
      }
      const cutIdx = searchTime(base, startDate);
      const contextCandles = normalizeCandles(sliceToCandles(base, 0, cutIdx));

      if (contextCandles.length > 0) {
        useReplayStore.getState().setContextCandles(contextCandles, newGranularitySeconds);
      }
    } catch (err) {
      console.warn("Context refresh failed", err);
    } finally {
      useReplayStore.getState().clearRefreshingContext();
    }
  }, [timeframe, isActive, startDate, contextGranularitySeconds, isRefreshingContext]);
}
